import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { format } from "date-fns";
import ReportTile from "./components/ReportTile";
import ReportMapView from "./components/ReportMapView";
import Card from "components/card";
import ReportPieChart from "./components/ReportPieChart";
import ReportFleetView from "./components/ReportFleetView";
import ReportDonutChart from "./components/ReportDonutChart";
import ReportLineGraph from "./components/ReportLineGraph";
import KeyPlot from "./components/KeyPlot";
import DeviceDetailsTable from "./components/DeviceDetailsTable";
import NoData from "../../../assets/img/dashboards/9404370.webp";
import ReportSummary from "./components/ReportSummary";
import NotReporting from "./components/NotReporting";
import ReportFleetMap from "./components/ReportFleetMap";
import "./report.css";
import { readToken } from "utils/session";
import DeviceSelect from "components/DeviceSelect";
import ReportView from "./components/ReportView";
import { downloadReportViewPdf, downloadVisibleReportPdf } from "./downloadReportPdf";
import { downloadReportViewExcel, downloadVisibleReportExcel } from "./downloadReportExcel";

const Report = () => {
  const [isOpen1, setIsOpen1] = useState(false);
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [selectedOption, setSelectedOption] = useState(null);
  const [devices, setDevices] = useState([]);
  const [reports, setReports] = useState([]);
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [periodLimit, setPeriodLimit] = useState(1);
  const [didCount, setDidCount] = useState(0);
  const [showPopup, setShowPopup] = useState(false);
  const [popupMessage, setPopupMessage] = useState("");
  const [showReport, setShowReport] = useState(false);
  const [showDownload, setShowDownload] = useState(false);
  const [downloadOpen, setDownloadOpen] = useState(false);
  const [cardSliderInfo, setCardSliderInfo] = useState([]);
  const [fleetInfo, setFleetInfo] = useState([]);
  const [fleetInfoDisplayNames, setFleetInfoDisplayNames] = useState({});
  const [pieInfo, setPieInfo] = useState([]);
  const [donutInfo, setDonutInfo] = useState([]);
  const [lineGraphInfo, setLineGraphInfo] = useState([]);
  const [tempInfo, setTempInfo] = useState([]);
  const [tempInfoDisplayNames, setTempInfoDisplayNames] = useState([]);
  const [tempInfoName, setTempInfoName] = useState("");
  const [utilInfo, setUtilInfo] = useState([]);
  const [utilInfoDisplayNames, setUtilInfoDisplayNames] = useState([]);
  const [utilInfoName, setUtilInfoName] = useState("");
  const [repDevInfo, setRepDevInfo] = useState([]);
  const [repDevInfoDisplayNames, setRepDevInfoDisplayNames] = useState([]);
  const [repDevInfoName, setRepDevInfoName] = useState("");
  const [notReportInfo, setNotReportInfo] = useState([]);
  const [notReportInfoDisplayNames, setNotReportInfoDisplayNames] = useState([]);
  const [notReportInfoName, setNotReportInfoName] = useState("");
  const [mapInfo, setMapInfo] = useState({});
  const [overlayImg, setOverlayImg] = useState("");
  const [mapPlotSource, setMapPlotSource] = useState([]);
  const [reportView, setReportView] = useState(null);
  const [fetchedAt, setFetchedAt] = useState("");

  const USERKEY = readToken();
  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;

  useEffect(() => {
    const inputReports = async () => {
      try {
        let config = {
          method: "get",
          maxBodyLength: Infinity,
          url: `${apiBaseUrl}/api/reports`,
          headers: {
            Authorization: `Bearer ${USERKEY}`,
          },
        };

        const response = await axios.request(config);
        const fleetData = response.data.reportsSupported;

        const reportData = fleetData.map((support) => ({
          didCount: support.didCount,
          maxDuration: support.maxDuration,
          periodLimit: support.periodLimit,
          reportId: support.reportId,
          reportName: support.reportName,
          reqFromDate: support.reqFromDate,
          reqToDate: support.reqToDate,
        }));

        setReports(reportData);
      } catch (error) {
        console.log(error);
      }
    };

    inputReports();
  }, [USERKEY, apiBaseUrl]);

  const dropdownRef1 = useRef(null);
  const downloadRef = useRef(null);

  const toggleDropdown1 = () => {
    setIsOpen1(!isOpen1);
  };

  const handleOptionClick = (option) => {
    const selectedReport = reports.find(
      (report) => report.reportName === option
    );
    if (selectedReport) {
      setSelectedOption(option);
      setPeriodLimit(selectedReport.periodLimit);
      setDidCount(selectedReport.didCount);
      setFromDate(null);
      setToDate(null);
      setSelectedOptions([]);
      setIsOpen1(false);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef1.current && !dropdownRef1.current.contains(event.target)) {
        setIsOpen1(false);
      }
      if (downloadRef.current && !downloadRef.current.contains(event.target)) {
        setDownloadOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const fetchDevices = async () => {
      try {
        let config = {
          method: "get",
          maxBodyLength: Infinity,
          url: `${apiBaseUrl}/api/fleetlist`,
          headers: {
            Authorization: `Bearer ${USERKEY}`,
          },
        };

        const response = await axios.request(config);
        const fleetData = response.data.fleetlist;

        const devicesData = fleetData.map((device) => ({
          aid: device.aid,
          did: device.did,
        }));

        setDevices(devicesData);
      } catch (error) {
        console.log(error);
      }
    };

    fetchDevices();
  }, [USERKEY, apiBaseUrl]);

  const handleViewReport = async () => {
    const currentReport = reports.find(
      (report) => report.reportName === selectedOption
    );

    if (!currentReport) {
      setPopupMessage("Please select a report");
      setShowPopup(true);
      setTimeout(() => setShowPopup(false), 3000);
      return;
    }

    if (didCount === 1 && selectedOptions.length === 0) {
      setPopupMessage("Please select one device ID");
      setShowPopup(true);
      setTimeout(() => setShowPopup(false), 3000);
      return;
    } else if (didCount > 1 && selectedOptions.length === 0) {
      setPopupMessage("Please select at least one device ID");
      setShowPopup(true);
      setTimeout(() => setShowPopup(false), 3000);
      return;
    }

    const { reqFromDate, reqToDate } = currentReport;

    if (reqFromDate && !fromDate) {
      setPopupMessage("From Date is required");
      setShowPopup(true);
      setTimeout(() => setShowPopup(false), 3000);
      return;
    }

    if (reqToDate && !toDate) {
      setPopupMessage("To Date is required");
      setShowPopup(true);
      setTimeout(() => setShowPopup(false), 3000);
      return;
    }

    const reportData = {
      reportId: currentReport.reportId.toString(),
      repFrom: fromDate ? format(fromDate, "yyyy-MM-dd") : "",
      repTo: toDate ? format(toDate, "yyyy-MM-dd") : "",
      repDid: selectedOptions,
    };

    // Make API request with dynamic parameters
    let config = {
      method: "get",
      maxBodyLength: Infinity,
      url: `${apiBaseUrl}/api/fetchreports?reportData=${encodeURIComponent(
        JSON.stringify(reportData)
      )}`,
      headers: {
        Authorization: `Bearer ${USERKEY}`,
      },
    };

    try {
      const response = await axios.request(config);
      setFetchedAt(new Date().toLocaleString());

      if (response.data?.reportView) {
        setReportView(response.data.reportView);
        setShowReport(true);
        setShowDownload(true);
        return;
      }

      setReportView(null);
      setCardSliderInfo(response.data.plotData?.cardSliderInfo?.cardSliderData || []);
      setFleetInfo(response.data.reportData?.fleetInfo?.fleetData || []);
      setFleetInfoDisplayNames(response.data.reportData?.fleetInfo?.fleetdisplaynames || {});
      setPieInfo(response.data.plotData?.PieChartInfo?.PieCharData || []);
      setDonutInfo(response.data.plotData?.DonutChartInfo?.DonutChartData || []);

      // Structure data for the line graph safely
      const lineChartData = response.data.plotData?.LineChartInfo?.LineChartData || [];
      const chartConfigs = lineChartData.map((chartData) => {
        const routeData = response.data.reportData?.mapInfo?.routeData || [];
        return {
          plotName: chartData.plotData.plotName,
          plottype: chartData.plottype,
          lineGraphInfo: {
            timestamps: routeData.map((item) => item.timestamp),
            datasets: chartData.plotData.yaxisData.map((yKey) => ({
              label: chartData.plotData.displayname[yKey],
              data: routeData.map((route) => route[yKey]),
            })),
          },
        };
      });

      setLineGraphInfo(chartConfigs);

      // ----- DYNAMIC TABLE PARSING (GENERIC UI) -----
      const tableData = response.data.plotData?.tableInfo?.tableData || [];

      // Safely grab table 1 (if it exists)
      setTempInfo(tableData[0]?.contents || []);
      setTempInfoDisplayNames(tableData[0]?.columns || {});
      setTempInfoName(tableData[0]?.tableName || "");

      // Safely grab table 2 (if it exists)
      setUtilInfo(tableData[1]?.contents || []);
      setUtilInfoDisplayNames(tableData[1]?.columns || {});
      setUtilInfoName(tableData[1]?.tableName || "");

      // Safely grab table 3 (if it exists)
      setRepDevInfo(tableData[2]?.contents || []);
      setRepDevInfoDisplayNames(tableData[2]?.columns || {});
      setRepDevInfoName(tableData[2]?.tableName || "");

      // Safely grab table 4 (if it exists)
      setNotReportInfo(tableData[3]?.contents || []);
      setNotReportInfoDisplayNames(tableData[3]?.columns || {});
      setNotReportInfoName(tableData[3]?.tableName || "");

      // Maps
      setMapInfo(response.data.reportData?.mapInfo?.routeData || {});
      setOverlayImg(response.data.reportData?.mapInfo?.deviceDetails?.AssetMapImg || "");
      setMapPlotSource(response.data.reportData?.mapPlot?.mapPlotSource || [false, false]);

      setShowReport(true);
      setShowDownload(true);
    } catch (error) {
      setReportView(null);
      setFetchedAt("");
      setShowReport(false);
      setShowDownload(false);
      setPopupMessage("No data available for the selected report");
      setShowPopup(true);
      setTimeout(() => setShowPopup(false), 3000);
    }
  };

  const handleDownload = async (kind) => {
    setDownloadOpen(false);
    try {
      if (reportView) {
        if (kind === "excel") await downloadReportViewExcel(reportView, fetchedAt);
        else await downloadReportViewPdf(reportView, fetchedAt);
        return;
      }
      const element = document.getElementById("reportContent");
      if (!element) {
        console.error("No content found to capture");
        return;
      }
      if (kind === "excel") await downloadVisibleReportExcel(element, fetchedAt);
      else await downloadVisibleReportPdf(element, fetchedAt);
    } catch (error) {
      console.error("Error generating report:", error);
    }
  };

  const currentReport = reports.find((report) => report.reportName === selectedOption) || null;
  const hideDates = currentReport
    && !currentReport.reqFromDate
    && !currentReport.reqToDate
    && Number(currentReport.periodLimit) <= 1;

  return (
    <>
      <Card extra="!flex-col lg:!flex-row flex-grow items-center rounded-[6px] relative p-5 mt-5 justify-between">
        {/* Dropdown for Reports */}
        <div
          ref={dropdownRef1}
          className="relative mb-2 inline-block w-full text-left sm:w-60 md:mb-0"
        >
          <div>
            <button
              type="button"
              onClick={toggleDropdown1}
              className="inline-flex w-full justify-between rounded-md bg-lightPrimary px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:bg-navy-900"
            >
              {selectedOption ? selectedOption : "Select Reports"}
              <svg
                className="-mr-1 ml-2 h-5 w-5"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M5.292 7.707a1 1 0 011.414 0L10 11.002l3.293-3.295a1 1 0 011.414 1.414l-4 4a 1 1 0 01-1.414 0l-4-4a 1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
            </button>
          </div>

          {isOpen1 && (
            <div className="ring-black absolute z-20 mt-2 w-full origin-top-right rounded-md bg-lightPrimary shadow-lg ring-1 ring-opacity-5 dark:bg-navy-900">
              <div className="py-1">
                {reports.map((report, index) => (
                  <div
                    key={index}
                    onClick={() => handleOptionClick(report.reportName)}
                    className="cursor-pointer px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 dark:hover:bg-navy-800"
                  >
                    {report.reportName}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Dropdown for Device ID */}
        {didCount > 0 ? (
          <DeviceSelect
            devices={devices}
            value={selectedOptions}
            multiple={didCount > 1}
            onChange={setSelectedOptions}
          />
        ) : null}

        {/* Date Picker and View Report Button */}
        <div className="flex flex-col items-center space-y-4 sm:flex-row sm:space-x-2 sm:space-y-0">
          {hideDates ? null : periodLimit > 1 ? (
            <div className="flex space-x-2">
              <DatePicker
                selected={fromDate}
                onChange={(date) => setFromDate(date)}
                className="z-10 w-full rounded-md bg-lightPrimary p-2 font-medium text-gray-700 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-navy-900 sm:w-40"
                placeholderText="From Date"
                dateFormat="yyyy/MM/dd"
              />
              <DatePicker
                selected={toDate}
                onChange={(date) => setToDate(date)}
                className="w-full rounded-md bg-lightPrimary p-2 font-medium text-gray-700 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-navy-900 sm:w-40"
                placeholderText="To date"
                dateFormat="yyyy/MM/dd"
              />
            </div>
          ) : (
            <DatePicker
              selected={toDate}
              onChange={(date) => setToDate(date)}
              className="w-full rounded-md bg-lightPrimary p-2 font-medium text-gray-700 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-navy-900 sm:w-60"
              placeholderText="Select a date"
              dateFormat="yyyy/MM/dd"
              maxDate={periodLimit === 1 ? new Date() : null}
            />
          )}

          <button
            type="button"
            onClick={handleViewReport}
            className="ml-3 rounded-md bg-blue-500 px-4 py-2 text-white hover:bg-blue-600"
          >
            View Report
          </button>
          {showDownload && (
            <div className="relative ml-3" ref={downloadRef}>
              <button
                type="button"
                aria-expanded={downloadOpen}
                onClick={() => setDownloadOpen((open) => !open)}
                className="inline-flex items-center gap-1 rounded-md bg-blue-500 px-4 py-2 text-white hover:bg-blue-600"
              >
                Download
                <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.17l3.71-3.94a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clipRule="evenodd" />
                </svg>
              </button>
              {downloadOpen && (
                <div className="absolute right-0 z-30 mt-2 w-36 overflow-hidden rounded-md bg-white shadow-lg ring-1 ring-black/10">
                  <button
                    type="button"
                    onClick={() => handleDownload("pdf")}
                    className="block w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100"
                  >
                    PDF
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDownload("excel")}
                    className="block w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100"
                  >
                    Excel
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </Card>

      {showPopup && (
        <div className="mt-5 flex justify-end">
          <div className="rounded-md bg-red-500 p-4 text-white">
            {popupMessage}
          </div>
        </div>
      )}

      {showReport && reportView ? (
        <div id="reportContent">
          <ReportView view={reportView} />
        </div>
      ) : showReport ? (
        <div id="reportContent">
          <div className="mt-5 grid h-full grid-cols-1 gap-5 md:grid-cols-1">
            
            {cardSliderInfo.length > 0 && (
              <ReportTile cardSliderInfo={cardSliderInfo} />
            )}

            {/* Donut and Pie - Half and Half */}
            {(donutInfo.length > 0 || pieInfo.length > 0) && (
              <div className="grid h-full grid-cols-1 gap-5 md:grid-cols-2">
                {donutInfo.length > 0 && <ReportDonutChart donutInfo={donutInfo} />}
                {pieInfo.length > 0 && <ReportPieChart pieInfo={pieInfo} />}
              </div>
            )}

            {/* Line Graph - Full Width */}
            {lineGraphInfo.length > 0 && (
              <div className="w-full">
                <ReportLineGraph lineGraphInfo={lineGraphInfo} />
              </div>
            )}

            {mapPlotSource[0] && (
              <ReportMapView mapInfo={mapInfo} overlayImg={overlayImg} />
            )}

            {fleetInfo.length > 0 && (
              <ReportFleetView
                rowsPerPage={5}
                fleetInfo={fleetInfo}
                fleetInfoDisplayNames={fleetInfoDisplayNames}
              />
            )}

            {mapPlotSource[1] && (
              <ReportFleetMap fleetInfo={fleetInfo} overlayImg={overlayImg} />
            )}

            {/* Dynamic Data Tables */}
            <div className="mt-5 grid h-full grid-cols-1 gap-5 md:grid-cols-2">
              {tempInfo.length > 0 && (
                <ReportSummary
                  rowsPerPage={5}
                  tempInfoName={tempInfoName}
                  tempInfo={tempInfo}
                  tempInfoDisplayNames={tempInfoDisplayNames}
                />
              )}
              {utilInfo.length > 0 && (
                <KeyPlot
                  rowsPerPage={5}
                  utilInfoName={utilInfoName}
                  utilInfo={utilInfo}
                  utilInfoDisplayNames={utilInfoDisplayNames}
                  mapInfo={mapInfo}
                />
              )}
              {repDevInfo.length > 0 && (
                <DeviceDetailsTable
                  rowsPerPage={5}
                  repDevInfoName={repDevInfoName}
                  repDevInfo={repDevInfo}
                  repDevInfoDisplayNames={repDevInfoDisplayNames}
                  mapInfo={mapInfo}
                />
              )}
              {notReportInfo.length > 0 && (
                <NotReporting
                  rowsPerPage={5}
                  notReportInfo={notReportInfo}
                  notReportInfoDisplayNames={notReportInfoDisplayNames}
                  notReportInfoName={notReportInfoName}
                  fleetInfo={fleetInfo}
                />
              )}
            </div>
          </div>
        </div>
      ) : (
        <div className="initialMessage flex flex-col items-center justify-center text-center">
          <img src={NoData} alt="noData" />
          <h4 className="mt-[-5rem] text-xl font-bold text-blue-1000 dark:text-white">
            Doesn't show the report
          </h4>
          <p className="mb-6 font-dm text-sm font-medium text-gray-600">
            Please select Report and more
          </p>
        </div>
      )}
    </>
  );
};

export default Report;